<script setup>

    import SessionsCard from '~/components/blocks/two-six/sessions/card.vue';
    import CompactSessionCard from '~/components/blocks/two-six/sessions/compact-session-card.vue';

    const props = defineProps({
        data: {
            type: Object,
            required: true,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        showFullCard: {
            type: Boolean,
            default: false,
        },
        disableLink: {
            type: Boolean,
            default: false
        },
        compact: {
            type: Boolean,
            default: false,
        },
        infinite: {
            type: Boolean,
            default: false,
        },
    });

    const emit = defineEmits(['changePage', 'load', 'loadMore']);
    const page = ref(1);
    const sentinel = ref(null);

    const hasMore = computed(() => {
        if (!props.data) return false;
        const current = parseInt(props.data.current_page || 1, 10);
        const total = parseInt(props.data.pages || 1, 10);
        return current < total;
    });

    const handlePageChange = (newPage) => {
        page.value = newPage;
        emit('changePage', newPage);
    };

    const handleSessionClick = (session) => {
        if (props.disableLink) {
            emit('load', session);
            return;
        }
    };

    const isSentinelInView = () => {
        const el = sentinel.value;
        if (!el) return false;
        const rect = el.getBoundingClientRect();
        const viewH = window.innerHeight;
        return rect.top < viewH + 400 && rect.bottom > -400;
    };

    // useIntersectionObserver reactively re-attaches when the sentinel ref changes,
    // which matters when hasMore toggles and remounts the sentinel element (e.g.
    // during client-side navigation where data populates after setup).
    useIntersectionObserver(
        sentinel,
        ([entry]) => {
            if (!props.infinite) return;
            if (entry?.isIntersecting && hasMore.value) emit('loadMore');
        },
        { rootMargin: '400px 0px' },
    );

    // Tall viewports: sentinel may stay in view through multiple loads so the
    // observer only fires once. After each load completes, re-check and emit
    // again if still in view.
    watch(() => props.loading, (newLoading, oldLoading) => {
        if (!props.infinite) return;
        if (oldLoading && !newLoading && hasMore.value) {
            nextTick(() => {
                if (isSentinelInView()) emit('loadMore');
            });
        }
    });

</script>
<template>
    <div class="sessions-grid" :class="{ 'pointer-events-none': loading }">
        <ul v-if="data.sessions?.length"
            class="grid gap-[1.25rem] grid-cols-[minmax(16rem,1fr)] mb-[1.75rem]
                lg:grid-cols-[repeat(2,1fr)] lg:mb-0
                xl:grid-cols-[repeat(auto-fill,minmax(22rem,1fr))]">
            <li v-for="session in data.sessions" :key="session.id" :data-page="session._page || 1">
                <CompactSessionCard v-if="compact" :session="session" :disableLink="disableLink" @load="handleSessionClick" />
                <SessionsCard v-else :session="session" layout="slim" show_sponsors show_tags hide_featured :full="showFullCard" :disableLink="disableLink" @load="handleSessionClick" />
            </li>
        </ul>
        <div v-else class="flex flex-col items-center justify-center lg:min-h-[20rem] text-(--magenta)">
            <h2 class="font-hubspot-sans">No Sessions Found</h2>
            <p class="font-hubspot-sans">Try adjusting your filters.</p>
        </div>
        <div v-if="infinite && hasMore" ref="sentinel" class="relative flex items-center justify-center py-[2rem] min-h-[80px]" aria-hidden="true">
            <FragmentsLoading :loading="loading" />
        </div>
        <div v-if="data.pages > 1" :class="infinite ? 'sr-only' : ''">
            <ProjectPagination
                :limit="limit"
                :totalPages="parseInt(data.pages)"
                :currentPage="parseInt(data.current_page)"
                @changePage="handlePageChange"
                :loading="false"
            />
        </div>
    </div>
</template>

<style>
/* Child component targeting — session card styles within grid context */
.sessions-grid .session-card {
    height: 100%;
}

.sessions-grid .session-card .session-tags-list:empty {
    margin-top: -1rem;
}

.sessions-grid .session-card h3 {
    font-size: calc(30 / var(--base-fs) * 1rem);
    font-weight: 400;
    line-height: 2.25rem;
    letter-spacing: 0;
}

@media (min-width: 1024px) {
    .sessions-grid .session-card h3 {
        margin-bottom: 2rem;
    }
}

.sessions-grid .session-card .speakers {
    margin-bottom: 1.1875rem;
}

@media (min-width: 1024px) {
    .sessions-grid .session-card .speakers {
        margin-bottom: 2rem;
    }
}

/* Context-dependent override */
.schedule-day .sessions-grid ul {
    margin-bottom: 0;
}
</style>
